import { useEffect, useState } from 'react';

const HeroSection = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleScrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const nodes = [
    { top: '15%', left: '8%', size: 48, delay: '0s', label: 'Intake' },
    { top: '28%', left: '18%', size: 36, delay: '0.4s', label: '' },
    { top: '60%', left: '6%', size: 40, delay: '0.8s', label: 'CRM' },
    { top: '75%', left: '20%', size: 32, delay: '1.2s', label: '' },
    { top: '20%', right: '10%', size: 44, delay: '0.2s', label: 'Output' },
    { top: '45%', right: '6%', size: 36, delay: '0.6s', label: '' },
    { top: '70%', right: '14%', size: 40, delay: '1s', label: 'Notify' },
  ];

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f0e2e 0%, #1E1B4B 45%, #2d1b69 100%)' }}
    >
      {/* Background image layer */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/083503adce6d73594d84d94980d8b568.jpg"
          alt=""
          className="w-full h-full object-cover object-top opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f0e2e]/95 via-[#1E1B4B]/85 to-[#2d1b69]/90" />
      </div>

      {/* Decorative node elements */}
      {nodes.map((node, i) => (
        <div
          key={i}
          className="absolute z-10 rounded-full border border-[#7C3AED]/30 flex items-center justify-center"
          style={{
            top: node.top,
            left: (node as any).left,
            right: (node as any).right,
            width: node.size,
            height: node.size,
            background: 'radial-gradient(circle, rgba(124,58,237,0.15) 0%, rgba(124,58,237,0.05) 100%)',
            animation: `float 4s ease-in-out infinite`,
            animationDelay: node.delay,
            opacity: visible ? 1 : 0,
            transition: `opacity 0.8s ease ${node.delay}`,
          }}
        >
          <div className="w-2 h-2 rounded-full bg-brand-violet/60" />
          {node.label && (
            <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] text-white/40 font-inter whitespace-nowrap">
              {node.label}
            </span>
          )}
        </div>
      ))}

      {/* Decorative connection lines */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute top-[22%] left-[11%] w-[7%] h-px bg-gradient-to-r from-brand-violet/20 to-transparent" />
        <div className="absolute top-[48%] left-[9%] w-[5%] h-px bg-gradient-to-r from-brand-teal/20 to-transparent" />
        <div className="absolute top-[26%] right-[13%] w-[6%] h-px bg-gradient-to-l from-brand-violet/20 to-transparent" />
        <div className="absolute top-[68%] right-[16%] w-[5%] h-px bg-gradient-to-l from-brand-teal/20 to-transparent" />
      </div>

      {/* Gradient orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 rounded-full bg-brand-violet/10 blur-3xl z-0" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-brand-teal/8 blur-3xl z-0" />

      {/* Main content */}
      <div className="relative z-20 w-full max-w-7xl mx-auto px-6 lg:px-8 py-32 flex flex-col items-center text-center">
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 border border-brand-teal/30 bg-brand-teal/10 backdrop-blur-sm"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s',
          }}
        >
          <div className="w-2 h-2 rounded-full bg-brand-teal animate-pulse" />
          <span className="font-inter text-xs font-semibold text-brand-teal tracking-widest uppercase">
            AI-Powered Workflow Automation
          </span>
        </div>

        {/* Headline */}
        <h1
          className="font-space font-semibold text-white leading-tight mb-6 max-w-4xl"
          style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease 0.4s, transform 0.7s ease 0.4s',
          }}
        >
          Turn Your Operations Into an{' '}
          <span
            className="relative"
            style={{
              background: 'linear-gradient(135deg, #7C3AED, #14B8A6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Automated System
          </span>
        </h1>

        {/* Subheadline */}
        <p
          className="font-inter text-white/65 leading-relaxed max-w-2xl mb-10"
          style={{
            fontSize: '1.125rem',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease 0.6s, transform 0.7s ease 0.6s',
          }}
        >
          Workflow Wizards AI builds intelligent workflows that eliminate inefficiencies, automate repetitive work, and help service-based businesses scale with clarity.
        </p>

        {/* CTA Buttons */}
        <div
          className="flex flex-wrap gap-4 justify-center"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease 0.8s, transform 0.7s ease 0.8s',
          }}
        >
          <button
            onClick={() => handleScrollTo('#process')}
            className="whitespace-nowrap font-inter font-semibold text-white px-7 py-3.5 rounded-lg cursor-pointer transition-all duration-200 hover:opacity-90 hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, #7C3AED, #6D28D9)' }}
          >
            Book a Demo
          </button>
          <button
            onClick={() => handleScrollTo('#solution')}
            className="whitespace-nowrap font-inter font-semibold text-white/90 px-7 py-3.5 rounded-lg cursor-pointer border border-white/20 hover:bg-white/10 transition-all duration-200 backdrop-blur-sm"
          >
            See How It Works
          </button>
        </div>

        {/* Stats row */}
        <div
          className="mt-20 grid grid-cols-3 gap-8 max-w-2xl w-full"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 1.1s',
          }}
        >
          {[
            { value: '3x', label: 'Faster Response Time' },
            { value: '80%', label: 'Reduction in Manual Work' },
            { value: '100+', label: 'Workflows Deployed' },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col items-center gap-1 border-r border-white/10 last:border-0">
              <span
                className="font-space font-semibold text-white"
                style={{ fontSize: '2rem' }}
              >
                {stat.value}
              </span>
              <span className="font-inter text-xs text-white/50 text-center leading-snug">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 opacity-50">
        <span className="font-inter text-xs text-white/50">Scroll to explore</span>
        <div className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>
  );
};

export default HeroSection;
